// src/utils/CardPreprocessor.js

/**
 * Utility to preprocess cards for different question types
 * based on their content and metadata
 */
class CardPreprocessor {
  /**
   * Process an array of learning queue cards to add appropriate question types
   * 
   * @param {Array} cards - Array of cards from the learning queue
   * @param {Object} options - Processing options (shadowing, tokenMode)
   * @returns {Array} Processed cards with appropriate question types
   */
  static processCards(cards, options = {}) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return [];
    }

    const {
      enableShadowing = true,
      enableAudioTranscription = true,
      enableAudioTranslation = false, // Supress for now - too many questions
      tokenMode = 'easy'
    } = options;

    console.log('[Debug] Processing cards with options:', {
      enableShadowing,
      enableAudioTranscription,
      enableAudioTranslation,
      tokenMode,
      cardCount: cards.length
    });

    // Process each card individually and create variants based on type and options
    const processedCards = cards.map(card => this.processCard(card, {
      enableShadowing,
      enableAudioTranscription,
      enableAudioTranslation,
      tokenMode
    }));

    // Enhanced debugging: Count each type of variant
    let audioTranscriptionCount = 0;
    let audioTranslationCount = 0;
    let shadowingCount = 0;
    let tokenReorderCount = 0;
    let textCount = 0;

    processedCards.forEach(card => {
      if (!card.variants) return;

      card.variants.forEach(variant => {
        if (variant.questionType === 'audio' && variant.audioMode === 'transcribe') {
          audioTranscriptionCount++;
        } else if (variant.questionType === 'audio' && variant.audioMode === 'translate') {
          audioTranslationCount++;
        } else if (variant.questionType === 'shadowing') {
          shadowingCount++;
        } else if (variant.questionType === 'tokenReorder') {
          tokenReorderCount++;
        } else if (variant.questionType === 'text') {
          textCount++;
        }
      });
    });

    console.log(`[Debug] Created variants breakdown:`, {
      audioTranscriptionCount,
      audioTranslationCount,
      shadowingCount,
      tokenReorderCount,
      textCount,
      totalCards: processedCards.length
    });

    return processedCards;
  }

  /**
   * Process a single card to determine and add appropriate question types
   * 
   * @param {Object} card - A single card from the learning queue
   * @param {Object} options - Processing options (shadowing, tokenMode)
   * @returns {Object} Processed card with appropriate question type
   */

  static processCard(card, options = {}) {
    // Make a deep copy first to avoid reference issues
    const processedCard = JSON.parse(JSON.stringify(card));

    const {
      enableShadowing = true,
      enableAudioTranscription = true,
      enableAudioTranslation = true,
      tokenMode = 'easy'
    } = options;

    // Check if this is a sentence card (has a sentenceId in metadata)
    const isSentence = this.isSentenceCard(card);

    // Check if this is a vocabulary word (has type: "vocabulary" in metadata)
    const isVocabulary = this.isVocabularyCard(card);

    // Improved audio detection - more robust check
    const hasAudio = this.hasAudioData(card);

    // Add explicit tracking of card type for debugging
    const cardType = isSentence ? "sentence" : isVocabulary ? "vocabulary" : "other";

    // Initialize variants array if it doesn't exist
    processedCard.variants = [];

    if (isSentence) {
      // For sentences, we'll create different question versions

      // Check if we're translating from English to foreign language or vice versa
      const isEnglishTitle = card.title === card.answer;

      if (!isEnglishTitle) {
        // For foreign -> English, always use text input
        const textInputCard = this.createTextInputCard(card);
        processedCard.variants.push(textInputCard);
      } else {
        // Temporarily deprecating audio -> english to reduce the number of questions
        
        // For English -> foreign, use mode-dependent input method

        /*if (tokenMode === 'easy') {
          // Easy mode: Token reordering for input
          const tokenReorderCard = this.createTokenReorderCard(card);
          processedCard.variants.push(tokenReorderCard);
        } else {
          // Hard mode: Text input without assistance
          const hardTextCard = this.createHardTextInputCard(card);
          processedCard.variants.push(hardTextCard);
        }*/
      }

      // Audio variant handling
      if (hasAudio) {
        // Add audio transcription card (transcribe foreign) - for all modes
        if (enableAudioTranscription) {
          const transcriptionCard = this.createAudioCard(card, 'transcribe', tokenMode);
          // Verify audio data is preserved
          this.verifyAudioExists(transcriptionCard, card);
          processedCard.variants.push(transcriptionCard);
        }

        // Add audio translation card (translate to English) - for all modes
        if (enableAudioTranslation) {
          const translationCard = this.createAudioCard(card, 'translate', tokenMode);
          // Verify audio data is preserved
          this.verifyAudioExists(translationCard, card);
          processedCard.variants.push(translationCard);
        }

        // Add shadowing card (practice speaking) if enabled
        if (enableShadowing) {
          const shadowingCard = this.createShadowingCard(card);
          // Verify audio data is preserved
          this.verifyAudioExists(shadowingCard, card);
          processedCard.variants.push(shadowingCard);
        }
      }

      // FIX: Don't set question type on the main card if it has variants
      // This prevents duplication when flattening cards
      processedCard.isVariantContainer = true;
      processedCard.questionType = "container";
      delete processedCard.question; // Remove question from main card
    } else if (isVocabulary) {
      // For vocabulary words, ALWAYS set to text type and NEVER add shadowing variants
      // regardless of whether they have audio or not

      // Always keep vocabulary as plain text
      processedCard.questionType = "text";

      // No audio variants for vocabulary words
      // Leave the variants array empty
    } else {
      // Default to text for any other card types
      processedCard.questionType = "text";
    }

    // Verify primary card has audio data preserved as needed
    if (hasAudio) {
      this.verifyAudioExists(processedCard, card);
    }

    return processedCard;
  }

  /**
   * Utility method to check if a card is a sentence card
   */
  static isSentenceCard(card) {
    return card && card.metadata && card.metadata.sentenceId;
  }

  /**
   * Utility method to check if a card is a vocabulary card
   */
  static isVocabularyCard(card) {
    return card && card.metadata && card.metadata.type === "vocabulary";
  }

  /**
   * Enhanced method to check if a card has audio data
   * Updated to check for audioCdnUrl first (CDN URL approach)
   */
  static hasAudioData(card) {
    if (!card) return false;

    // Enhanced audio detection with detailed logging
    const hasAudioCdnUrl = Boolean(card.audioCdnUrl);
    const hasAudioUrl = Boolean(card.audioUrl);
    const hasAudioData = Boolean(card.audioData);
    const hasAudioObject = Boolean(card.audio);
    const hasAudioPath = hasAudioObject && typeof card.audio === 'object' && Boolean(card.audio.path);
    const hasAudioObjectUrl = hasAudioObject && Boolean(card.audio.url);

    const hasAudio = hasAudioCdnUrl || hasAudioUrl || hasAudioData || hasAudioPath || hasAudioObjectUrl;

    return hasAudio;
  }

  /**
   * Method to verify audio exists and log detailed info
   * Updated to prioritize audioCdnUrl
   */
  static verifyAudioExists(targetCard, sourceCard) {
    // Explicitly check if audio data was preserved
    const hasAudioCdnUrl = Boolean(targetCard.audioCdnUrl);
    const hasAudioUrl = Boolean(targetCard.audioUrl);
    const hasAudioData = Boolean(targetCard.audioData);
    const hasAudioObject = Boolean(targetCard.audio);
    const hasAudioPath = hasAudioObject && typeof targetCard.audio === 'object' && Boolean(targetCard.audio.path);
    const hasAudioObjectUrl = hasAudioObject && Boolean(targetCard.audio.url);

    const hasAudio = hasAudioCdnUrl || hasAudioUrl || hasAudioData || hasAudioPath || hasAudioObjectUrl;

    // If card needs audio but doesn't have it, copy it again and log warning
    if (!hasAudio) {
      console.warn(`[CRITICAL] Audio data was not preserved for ${targetCard.questionType} variant. Fixing...`);
      
      // First try to copy the audioCdnUrl (highest priority)
      if (sourceCard.audioCdnUrl) {
        targetCard.audioCdnUrl = sourceCard.audioCdnUrl;
      }
      // Then try audioUrl (second priority)
      else if (sourceCard.audioUrl) {
        targetCard.audioUrl = sourceCard.audioUrl;
      }
      // As backup, copy all audio properties from source
      targetCard.audioData = sourceCard.audioData;
      targetCard.audio = sourceCard.audio;
    }

    return hasAudio;
  }

  /**
   * Create a plain text input version of a card
   * 
   * @param {Object} card - The original card
   * @returns {Object} New card with text input settings
   */
  static createTextInputCard(card) {
    // Use deep copy instead of shallow copy to avoid reference issues
    const textCard = JSON.parse(JSON.stringify(card));

    // Set question-specific properties
    textCard.questionType = "text";
    textCard.question = `Translate to English: "${card.title}"`;

    // Make sure variantId is unique
    textCard.variantId = `${card.cardId || card._id}-text-${Math.random().toString(36).substring(2, 9)}`;

    // Ensure audio data is properly preserved
    this.preserveAudioData(textCard, card);

    return textCard;
  }

  /**
   * Create a hard mode text input version (no tokens assistance)
   * 
   * @param {Object} card - The original card
   * @returns {Object} New card with hard text input settings
   */
  static createHardTextInputCard(card) {
    // Use deep copy instead of shallow copy to avoid reference issues
    const hardTextCard = JSON.parse(JSON.stringify(card));

    hardTextCard.questionType = "text";
    hardTextCard.question = `Translate to foreign language without assistance: "${card.title}"`;

    // Make sure variantId is unique
    hardTextCard.variantId = `${card.cardId || card._id}-hardtext-${Math.random().toString(36).substring(2, 9)}`;

    // Ensure audio data is properly preserved
    this.preserveAudioData(hardTextCard, card);

    return hardTextCard;
  }

  /**
   * Create a token reorder version of a card
   * 
   * @param {Object} card - The original card
   * @returns {Object} New card with token reorder settings
   */
  static createTokenReorderCard(card) {
    // Use deep copy instead of shallow copy to avoid reference issues
    const tokenCard = JSON.parse(JSON.stringify(card));

    const isEnglishTitle = card.title === card.answer;

    tokenCard.questionType = "tokenReorder";
    // Make sure variantId is unique
    tokenCard.variantId = `${card.cardId || card._id}-tokens-${Math.random().toString(36).substring(2, 9)}`;

    if (isEnglishTitle) {
      // Case 1: Reconstruct foreign language from English
      tokenCard.tokens = this.tokenizeAndShuffle(card.answer);
    } else {
      // Case 2: Reconstruct English from foreign language - should not happen with new changes
      tokenCard.tokens = this.tokenizeAndShuffle(card.answer);
      tokenCard.question = `Translate to English: "${card.title}"`;
    }

    // Ensure audio data is properly preserved
    this.preserveAudioData(tokenCard, card);

    return tokenCard;
  }

  /**
   * Create an audio-based version of a card
   * Updated to prioritize audioCdnUrl
   * 
   * @param {Object} card - The original card
   * @param {string} mode - 'transcribe' or 'translate'
   * @param {string} tokenMode - 'easy' or 'hard', determining the input method
   * @returns {Object} New card with audio settings
   */
  static createAudioCard(card, mode = 'transcribe', tokenMode = 'easy') {
    // Use deep copy instead of shallow copy to avoid reference issues
    const audioCard = JSON.parse(JSON.stringify(card));

    // Always set the questionType to 'audio' and don't override it
    audioCard.questionType = 'audio';
    audioCard.audioMode = mode;

    // Make sure variantId is unique
    audioCard.variantId = `${card.cardId || card._id}-audio-${mode}-${Math.random().toString(36).substring(2, 9)}`;

    if (mode === 'transcribe') {
      // For transcription (same language)
      if (tokenMode === 'easy') {
        audioCard.tokens = this.tokenizeAndShuffle(card.title);
      }
      audioCard.question = "Listen and transcribe in the same language";
      audioCard.correctAudioAnswer = card.title;
    } else {
      // For translation (English)
      if (tokenMode === 'easy') {
        audioCard.tokens = this.tokenizeAndShuffle(card.answer);
      }
      audioCard.question = "Listen and translate to English";
      audioCard.correctAudioAnswer = card.answer;
    }

    // Ensure audio data is properly preserved - this is critical
    this.preserveAudioData(audioCard, card);

    // Add an explicit flag for audio type questions to make filtering easier
    audioCard.isAudioQuestion = true;

    return audioCard;
  }

  /**
   * Create a shadowing version of a card
   * Updated to prioritize audioCdnUrl
   * 
   * @param {Object} card - The original card
   * @returns {Object} New card with shadowing settings
   */
  static createShadowingCard(card) {
    console.log('Creating shadowing card from:', card);
    // Use deep copy instead of shallow copy to avoid reference issues
    const shadowCard = JSON.parse(JSON.stringify(card));

    shadowCard.questionType = "shadowing";
    // Make sure variantId is unique
    shadowCard.variantId = `${card.cardId || card._id}-shadowing-${Math.random().toString(36).substring(2, 9)}`;

    // We'll use the foreign language (title) for shadowing
    shadowCard.question = "Listen to the audio and repeat the sentence";
    shadowCard.correctAudioAnswer = card.title;

    // Determine the language based on metadata or default to German
    shadowCard.language = card.metadata?.language || 'de-DE';

    // Ensure audio data is properly preserved
    this.preserveAudioData(shadowCard, card);

    return shadowCard;
  }

  /**
   * Enhanced preserveAudioData to ensure all audio properties are properly copied
   * Updated to prioritize audioCdnUrl (CDN URL approach)
   * 
   * @param {Object} targetCard - The card to ensure has audio
   * @param {Object} sourceCard - The original card with audio data
   */
  static preserveAudioData(targetCard, sourceCard) {
    // Use a more structured approach to preserve ALL audio properties
    
    // 1. Preserve audioCdnUrl first (highest priority)
    if (sourceCard.audioCdnUrl !== undefined) {
      targetCard.audioCdnUrl = sourceCard.audioCdnUrl;
    }
    
    // 2. Preserve audioUrl next (legacy but still used)
    if (sourceCard.audioUrl !== undefined) {
      targetCard.audioUrl = sourceCard.audioUrl;
    }

    // 3. Preserve audioData as backup
    if (sourceCard.audioData !== undefined) {
      targetCard.audioData = sourceCard.audioData;
    }

    // 4. Preserve audio object and its properties
    if (sourceCard.audio !== undefined) {
      // Create a deep copy to avoid reference issues
      if (typeof sourceCard.audio === 'object' && sourceCard.audio !== null) {
        targetCard.audio = JSON.parse(JSON.stringify(sourceCard.audio));
      } else {
        targetCard.audio = sourceCard.audio;
      }
    }

    // For audio questions, ensure we have the right audioMode
    if (targetCard.questionType === 'audio') {
      targetCard.audioMode = targetCard.audioMode || 'transcribe';
      targetCard.isAudioQuestion = true;
    }

    // For shadowing questions, ensure we have the right language
    if (targetCard.questionType === 'shadowing') {
      targetCard.language = targetCard.language || sourceCard.metadata?.language || 'de-DE';
    }

    // Determine if audio is needed based on the question type
    const needsAudio =
      targetCard.questionType === "shadowing" ||
      targetCard.questionType === "audio" ||
      targetCard.audioMode === "transcribe" ||
      targetCard.audioMode === "translate" ||
      (targetCard.question && targetCard.question.includes("Listen to the audio"));

    // If the card needs audio but doesn't have it, log a warning
    if (needsAudio && !this.hasAudioData(targetCard)) {
      console.warn(`[CRITICAL WARNING] Card needs audio but doesn't have it: ${targetCard.questionType} - ${targetCard.question}`);
    }
  }

  /**
   * Tokenize a sentence and shuffle the tokens
   * 
   * @param {string} sentence - The sentence to tokenize
   * @returns {Array} Shuffled array of tokens
   */
  static tokenizeAndShuffle(sentence) {
    if (!sentence || typeof sentence !== 'string') {
      return [];
    }

    // First, separate punctuation from words so they become individual tokens
    // This regex adds spaces around punctuation
    const withSpacedPunctuation = sentence.replace(/([.,!?;:])/g, ' $1 ');

    // Split by whitespace and filter out empty tokens
    const tokens = withSpacedPunctuation
      .split(/\s+/)
      .filter(token => token.trim() !== '');

    // Return shuffled tokens
    return this.shuffleArray(tokens);
  }

  /**
   * Shuffle an array using Fisher-Yates algorithm
   * 
   * @param {Array} array - The array to shuffle
   * @returns {Array} Shuffled array
   */
  static shuffleArray(array) {
    if (!array || array.length <= 1) return array;

    // Fisher-Yates shuffle algorithm
    const shuffled = [...array];

    // Perform multiple passes for better randomization
    for (let pass = 0; pass < 3; pass++) {
      for (let i = shuffled.length - 1; i > 0; i--) {
        // Generate a more random index using crypto if available
        let j;
        if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
          const randomBuffer = new Uint32Array(1);
          window.crypto.getRandomValues(randomBuffer);
          j = Math.floor((randomBuffer[0] / (0xffffffff + 1)) * (i + 1));
        } else {
          j = Math.floor(Math.random() * (i + 1));
        }

        // Swap elements
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
    }

    return shuffled;
  }

  /**
   * Analyze review cards and return statistics
   * Updated to track audioCdnUrl in hasAudioData
   * 
   * @param {Array} cards - Array of cards from the learning queue
   * @returns {Object} Statistics about the cards (sentences, words, shadowingCount)
   */
  static analyzeReviewCards(cards) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return {
        sentenceCount: 0,
        wordCount: 0,
        shadowingCount: 0,
        audioTranscriptionCount: 0,
        audioTranslationCount: 0,
        totalCount: 0,
        sentenceVariantCount: {}, // Track variant counts per sentence
        questionTypeCounts: {
          text: 0,
          tokenReorder: 0,
          audio: 0,
          audioTranscription: 0,
          audioTranslation: 0,
          shadowing: 0,
          mcq: 0,
          other: 0
        }
      };
    }

    const sentenceIds = new Set();
    const sentenceVariantCount = {}; // To track how many variants each sentence has
    let wordCount = 0;
    let shadowingCount = 0;
    let audioTranscriptionCount = 0;
    let audioTranslationCount = 0;

    // Counts by question type
    const questionTypeCounts = {
      text: 0,
      tokenReorder: 0,
      audio: 0,
      audioTranscription: 0,
      audioTranslation: 0,
      shadowing: 0,
      mcq: 0,
      other: 0
    };

    cards.forEach(card => {
      const isSentence = this.isSentenceCard(card);
      const isVocabulary = this.isVocabularyCard(card);
      const hasAudio = this.hasAudioData(card);

      if (isSentence) {
        const sentenceId = card.metadata.sentenceId;
        sentenceIds.add(sentenceId);

        // Track how many variants this sentence has
        if (!sentenceVariantCount[sentenceId]) {
          sentenceVariantCount[sentenceId] = 0;
        }
        sentenceVariantCount[sentenceId]++;

        // Count by question type
        const qType = card.questionType || 'text';
        if (questionTypeCounts[qType] !== undefined) {
          questionTypeCounts[qType]++;
        } else {
          questionTypeCounts.other++;
        }

        // Count audio-specific types
        if (qType === 'audio') {
          if (card.audioMode === 'transcribe') {
            audioTranscriptionCount++;
            questionTypeCounts.audioTranscription++;
          } else if (card.audioMode === 'translate') {
            audioTranslationCount++;
            questionTypeCounts.audioTranslation++;
          }
        }

        // Count shadowing if applicable
        if (qType === 'shadowing' && hasAudio) {
          shadowingCount++;
        }
      } else if (isVocabulary) {
        wordCount++;

        // Count by question type for vocabulary
        const qType = card.questionType || 'text';
        if (questionTypeCounts[qType] !== undefined) {
          questionTypeCounts[qType]++;
        } else {
          questionTypeCounts.other++;
        }

        // Add shadowing for vocabulary with audio
        if (hasAudio && qType === 'shadowing') {
          shadowingCount++;
        }
      } else {
        // Count other question types
        const qType = card.questionType || 'text';
        if (questionTypeCounts[qType] !== undefined) {
          questionTypeCounts[qType]++;
        } else {
          questionTypeCounts.other++;
        }
      }
    });

    return {
      sentenceCount: sentenceIds.size,
      wordCount: wordCount,
      shadowingCount: shadowingCount,
      audioTranscriptionCount: audioTranscriptionCount,
      audioTranslationCount: audioTranslationCount,
      totalCount: sentenceIds.size + wordCount,
      sentenceVariantCount, // Include the variant count per sentence
      questionTypeCounts // Include counts by question type
    };
  }

  /**
   * Improved expandCardsWithVariants to properly handle audio variants
   * Updated to prioritize audioCdnUrl
   * 
   * @param {Array} cards - Original card array
   * @returns {Array} Expanded cards with variants as individual cards
   */
  static expandCardsWithVariants(cards) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return [];
    }

    // Track unique card IDs to prevent duplicates
    const seenCardIds = new Set();

    // Expand cards with variants
    const expandedCards = [];

    // Add debug counts
    let totalVariants = 0;
    let audioVariants = 0;
    let shadowingVariants = 0;

    cards.forEach(card => {
      // Make sure card has an ID
      const cardId = card.cardId || card._id;

      if (!cardId) {
        console.warn('[Warning] Card missing ID, skipping:', card);
        return;
      }

      // FIX: Skip the main card if it's a variant container
      // This prevents duplicate questions
      const skipMainCard = card.isVariantContainer || card.questionType === "container";

      if (card.variants && card.variants.length > 0) {
        totalVariants += card.variants.length;

        // Add each variant as a separate card with original metadata
        card.variants.forEach((variant, index) => {
          // Create a truly unique ID for each variant
          const variantId = variant.variantId || `${cardId}-${variant.questionType || 'unknown'}-${index}-${Math.random().toString(36).substring(2, 6)}`;

          // Track audio and shadowing variants
          if (variant.questionType === 'audio') {
            audioVariants++;
          }
          if (variant.questionType === 'shadowing') {
            shadowingVariants++;
          }

          // Only add if we haven't seen this variant ID before
          if (!seenCardIds.has(variantId)) {
            seenCardIds.add(variantId);

            // Properly merge variant properties with card properties
            const processedVariant = {
              ...variant,
              cardId: cardId, // Use the original card cardId
              variantId: variantId, // Add variant ID for tracking
              uniqueCardId: variantId, // Ensure uniqueCardId is set
              title: card.title,
              answer: card.answer,
              metadata: { ...card.metadata },
              steps: Array.isArray(card.steps) ? [...card.steps] : card.steps,
              originalCard: cardId, // Reference to the original card
              isVariant: true, // Explicitly mark as variant
              variantType: variant.questionType || 'text', // Track variant type
              currentLevel: card.currentLevel || 1 // Ensure currentLevel is copied
            };

            // Verify audio data exists for audio-based questions
            if (variant.questionType === 'audio' || variant.questionType === 'shadowing') {
              this.verifyAudioExists(processedVariant, card);
            }

            expandedCards.push(processedVariant);
          }
        });
      } 
      
      // Only add the main card if not a variant container and not already seen
      if (!skipMainCard && !seenCardIds.has(cardId)) {
        seenCardIds.add(cardId);
        expandedCards.push({
          ...card,
          cardId,
          uniqueCardId: cardId, // Ensure uniqueCardId is set
          isVariant: false
        });
      }
    });

    return expandedCards;
  }

  /**
   * Group and shuffle cards to ensure sentence-related questions aren't clustered together
   * Improved distribution to ensure audio questions are included
   * 
   * @param {Array} cards - Array of processed cards
   * @returns {Array} Shuffled array with distributed question types
   */
  static distributeAndShuffleCards(cards) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return [];
    }

    // Expand cards with their variants
    const expandedCards = this.expandCardsWithVariants(cards);

    // Log expanded cards count by type
    const typeCounts = {};
    expandedCards.forEach(card => {
      const type = card.questionType || 'unknown';
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });

    // First, identify all sentence groups and question type groups
    const sentenceGroups = {};
    const questionTypeGroups = {
      audio: [],
      shadowing: [],
      tokenReorder: [],
      text: [],
      other: []
    };

    expandedCards.forEach(card => {
      // Ensure token reorder cards have tokens
      if (card.questionType === 'tokenReorder' && !card.tokens && card.answer) {
        card.tokens = this.tokenizeAndShuffle(card.answer);
      }

      // Ensure audio cards have correct audio properties
      if (card.questionType === 'audio' || card.questionType === 'shadowing') {
        // Find the original card to reapply audio data if needed
        const originalCard = cards.find(c => c.cardId === card.cardId || c._id === card.cardId);
        if (originalCard) {
          this.verifyAudioExists(card, originalCard);
        }
      }

      // Group by sentence ID first
      if (card.metadata && card.metadata.sentenceId) {
        const sentenceId = card.metadata.sentenceId;
        if (!sentenceGroups[sentenceId]) {
          sentenceGroups[sentenceId] = [];
        }
        sentenceGroups[sentenceId].push(card);
      }

      // Also group by question type to ensure variety
      const qType = card.questionType || 'text';
      if (questionTypeGroups[qType]) {
        questionTypeGroups[qType].push(card);
      } else {
        questionTypeGroups.other.push(card);
      }
    });


    // Ensure we have a good mix of question types in the final result
    const result = [];

    // Prioritize audio and shadowing cards - ALWAYS include all of them
    ['audio', 'shadowing'].forEach(type => {
      if (questionTypeGroups[type].length > 0) {
        // Shuffle these special types first
        questionTypeGroups[type] = this.shuffleArray(questionTypeGroups[type]);

        // Add them to result first to ensure they're included
        result.push(...questionTypeGroups[type]);
      }
    });

    // Now add the remaining sentence groups, but filter out audio/shadowing that were already added
    const sentenceIds = Object.keys(sentenceGroups);
    const shuffledSentenceIds = this.shuffleArray([...sentenceIds]);

    shuffledSentenceIds.forEach(id => {
      // Shuffle each sentence group internally
      const shuffledGroup = this.shuffleArray(sentenceGroups[id]);

      // Filter out audio and shadowing questions that were already added
      const filteredGroup = shuffledGroup.filter(card =>
        !['audio', 'shadowing'].includes(card.questionType)
      );

      result.push(...filteredGroup);
    });

    // Add any remaining cards (token reorder, text, other)
    ['tokenReorder', 'text', 'other'].forEach(type => {
      if (questionTypeGroups[type].length > 0) {
        result.push(...this.shuffleArray(questionTypeGroups[type]));
      }
    });

    // Final verification to ensure no audio/shadowing cards were lost
    const audioCount = result.filter(card => card.questionType === 'audio').length;
    const shadowingCount = result.filter(card => card.questionType === 'shadowing').length;

    if (audioCount !== typeCounts['audio']) {
      console.warn(`[CRITICAL WARNING] Audio cards count mismatch: ${audioCount} vs ${typeCounts['audio']}`);
    }

    if (shadowingCount !== typeCounts['shadowing']) {
      console.warn(`[CRITICAL WARNING] Shadowing cards count mismatch: ${shadowingCount} vs ${typeCounts['shadowing']}`);
    }

    // Log final result by type
    const resultTypeCounts = {};
    result.forEach(card => {
      const type = card.questionType || 'unknown';
      resultTypeCounts[type] = (resultTypeCounts[type] || 0) + 1;
    });

    // Final shuffle for good measure
    return this.shuffleArray(result);
  }

  /**
   * Get variant count for a specific sentence
   * Used to determine how many variants need to be completed
   * 
   * @param {string} sentenceId - The sentence ID to check
   * @param {Array} cards - Array of all cards
   * @returns {number} Number of variants for this sentence
   */
  static getSentenceVariantCount(sentenceId, cards) {
    if (!sentenceId || !Array.isArray(cards) || cards.length === 0) {
      return 0;
    }

    let count = 0;

    for (const card of cards) {
      if (card.metadata && card.metadata.sentenceId === sentenceId) {
        count++;
      }
    }

    return count;
  }

  /**
   * Group cards by sentences and their component words
   * Useful for course learning with dynamic batch sizes
   * 
   * @param {Array} cards - Array of cards from the learning queue
   * @param {Object} options - Processing options (shadowing, tokenMode)
   * @returns {Array} Array of card batches, where each batch contains a sentence and its component words
   */
  static groupCardsBySentences(cards, options = {}) {
    if (!Array.isArray(cards) || cards.length === 0) {
      return [];
    }

    // First, process all cards to add question types with the specified options
    const processedCards = this.processCards(cards, options);

    // Expand cards with variants using our shared utility
    const expandedCards = this.expandCardsWithVariants(processedCards);

    // Now group them by sentences
    const batches = [];
    let currentBatch = [];
    const seenSentenceIds = new Set();

    expandedCards.forEach(card => {
      const isSentence = this.isSentenceCard(card);

      if (isSentence) {
        const sentenceId = card.metadata.sentenceId;

        // If it's a new sentence ID, start a new batch
        if (!seenSentenceIds.has(sentenceId)) {
          if (currentBatch.length > 0) {
            batches.push([...currentBatch]);
            currentBatch = [];
          }

          // Start a new batch and track the new sentence ID
          currentBatch.push(card);
          seenSentenceIds.add(sentenceId);
        } else {
          // If sentence ID was seen before, just continue adding
          currentBatch.push(card);
        }
      } else {
        // Non-sentence cards go into current batch
        currentBatch.push(card);
      }
    });

    // Push the last batch if any
    if (currentBatch.length > 0) {
      batches.push([...currentBatch]);
    }

    return batches;
  }
}

export default CardPreprocessor;